import { useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { GraduationCap, Presentation, School, Sparkles, CalendarDays } from "lucide-react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useDashboardOverview } from "../../query/dashboardQuery";
import { useTheme } from "../../hooks/useTheme";
import "./dashboard.css";

const CHART_FONT = "Poppins, sans-serif";
const PURPLE = "#5b4aa8";
const STUDENT = "#4aa8e8";
const TEACHER = "#fc7a3a";
const OVERVIEW_RANGES = [
  { id: "thisWeek", label: "This week", short: "Week" },
  { id: "lastWeek", label: "Last week", short: "Last" },
  { id: "thisMonth", label: "This month", short: "Month" },
  { id: "lastMonth", label: "Last month", short: "Prior" },
];
const BREAKDOWN_COLORS = {
  Present: "#4aa8e8",
  Absent: "#fc7a3a",
  Leave: "#f5c242",
  Holiday: "#5b4aa8",
};
const MARK_PRIORITY = { holiday: 3, event: 2, notice: 1, homework: 0 };
const HOLIDAY_COLOR = "#5b4aa8";
const EVENT_COLOR = "#fc7a3a";

function formatNumber(value) {
  return Number(value || 0).toLocaleString("en-US");
}

function formatScheduleRange(item) {
  const start = dayjs(item.date);
  const end = dayjs(item.endDate || item.date);
  if (!start.isValid()) return "";
  if (!end.isValid() || end.isSame(start, "day")) {
    return start.format("D MMM YYYY");
  }
  if (start.isSame(end, "month")) {
    return `${start.format("D")}–${end.format("D MMM YYYY")}`;
  }
  return `${start.format("D MMM")} – ${end.format("D MMM YYYY")}`;
}

function marksByDay(marks) {
  const map = {};
  (marks || []).forEach((mark) => {
    const key = mark.date || String(mark.day);
    if (!map[key]) map[key] = [];
    map[key].push(mark);
  });
  Object.keys(map).forEach((key) => {
    map[key].sort(
      (a, b) => (MARK_PRIORITY[b.type] || 0) - (MARK_PRIORITY[a.type] || 0)
    );
  });
  return map;
}

function chartTheme(isDark) {
  return {
    tick: isDark ? "#9aa3b5" : "#9aa3b5",
    grid: isDark ? "rgba(148, 163, 184, 0.22)" : "#E5E7EB",
    cursor: isDark ? "rgba(148,163,184,0.22)" : "rgba(148,163,184,0.32)",
  };
}

const STAT_CARDS = [
  {
    key: "students",
    label: "Students",
    icon: GraduationCap,
    tone: "purple",
  },
  {
    key: "teachers",
    label: "Teachers",
    icon: Presentation,
    tone: "pink",
  },
  {
    key: "classes",
    label: "Classes",
    icon: School,
    tone: "green",
  },
  {
    key: "activities",
    label: "Activities",
    icon: Sparkles,
    tone: "blue",
  },
];

function StatCard({ label, value, icon: Icon, tone, loading }) {
  return (
    <article className={`dash-stat dash-stat-${tone}`} title={label} aria-label={`${label}: ${loading ? "loading" : value}`}>
      <span className="dash-stat-blob" aria-hidden>
        <Icon size={20} strokeWidth={1.85} />
      </span>
      <div className="dash-stat-copy">
        <p className="dash-stat-label">{label}</p>
        {loading ? (
          <span className="dash-stat-bone" aria-hidden="true" />
        ) : (
          <p className="dash-stat-value">{value}</p>
        )}
      </div>
    </article>
  );
}

const TOOLTIP_DOT_COLORS = {
  students: STUDENT,
  teachers: TEACHER,
};

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  return (
    <div className="dash-chart-tooltip">
      {label ? <p className="dash-chart-tooltip-title">{label}</p> : null}
      {payload.map((entry) => (
        <div key={`${entry.dataKey}-${entry.name}`} className="dash-chart-tooltip-row">
          <span>
            <i
              style={{
                background:
                  TOOLTIP_DOT_COLORS[entry.dataKey] || entry.color || entry.payload?.fill,
              }}
            />
            {entry.name}
          </span>
          <strong>{formatNumber(entry.value)}</strong>
        </div>
      ))}
    </div>
  );
}

function PieTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const entry = payload.find((item) => item.name !== "track") || payload[0];
  if (!entry || entry.name === "track") return null;
  return (
    <div className="dash-chart-tooltip">
      <div className="dash-chart-tooltip-row">
        <span>
          <i style={{ background: entry.payload.fill }} />
          {entry.name}
        </span>
        <strong>{formatNumber(entry.value)}</strong>
      </div>
    </div>
  );
}

function CardHead({ title, extra }) {
  return (
    <div className="dash-card-head dash-card-head-min">
      <h2 className="dash-card-title">{title}</h2>
      {extra}
    </div>
  );
}

function WeeklyTrendChart({ series = {}, range, onRangeChange, isDark }) {
  const theme = chartTheme(isDark);
  const chartData = useMemo(() => {
    const labels = series.labels?.length
      ? series.labels
      : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const students = series.studentsPresent?.length ? series.studentsPresent : labels.map(() => 0);
    const teachers = series.teachersPresent?.length ? series.teachersPresent : labels.map(() => 0);
    return labels.map((day, index) => ({
      day,
      students: students[index] ?? 0,
      teachers: teachers[index] ?? 0,
    }));
  }, [series]);
  const xTicks = useMemo(() => {
    const labels = chartData.map((row) => row.day);
    const count = Math.min(labels.length, 7);
    if (labels.length <= count) return labels;
    return Array.from({ length: count }, (_, index) => {
      const at = Math.round((index * (labels.length - 1)) / (count - 1));
      return labels[at];
    });
  }, [chartData]);
  const { yMax, yTicks } = useMemo(() => {
    const max = chartData.reduce(
      (highest, row) => Math.max(highest, Number(row.students) || 0, Number(row.teachers) || 0),
      0
    );
    const top = Math.max(4, Math.ceil(max / 4) * 4);
    const step = top / 4;
    return { yMax: top, yTicks: [0, step, step * 2, step * 3, top] };
  }, [chartData]);

  return (
    <section className="dash-card dash-minimal-card dash-chart-panel dash-chart-hero">
      <div className="dash-card-head dash-card-head-min dash-hero-head">
        <h2 className="dash-card-title">Attendance overview</h2>
        <div className="dash-range-filters" role="tablist" aria-label="Attendance range">
          {OVERVIEW_RANGES.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={range === item.id}
              className={`dash-range-btn${range === item.id ? " is-active" : ""}`}
              onClick={() => onRangeChange(item.id)}
            >
              <span className="dash-range-full">{item.label}</span>
              <span className="dash-range-short">{item.short}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="dash-chart-box dash-chart-box-hero">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
            <defs>
              <linearGradient id="studentFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={STUDENT} stopOpacity={0.28} />
                <stop offset="100%" stopColor={STUDENT} stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="teacherFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={TEACHER} stopOpacity={0.22} />
                <stop offset="100%" stopColor={TEACHER} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid
              stroke={theme.grid}
              strokeWidth={1}
              vertical
              horizontal={false}
              verticalCoordinatesGenerator={({ width, offset }) => {
                const count = 13;
                const left = Number(offset?.left) || 0;
                const right = Number(offset?.right) || 0;
                const plotWidth = Number(offset?.width) || Math.max((width || 0) - left - right, 0);
                if (plotWidth <= 0) return [];
                return Array.from({ length: count }, (_, index) =>
                  left + (plotWidth * index) / (count - 1)
                );
              }}
            />
            <XAxis
              dataKey="day"
              axisLine={false}
              tickLine={false}
              interval={0}
              ticks={xTicks}
              padding={{ left: 0, right: 0 }}
              minTickGap={0}
              tick={{ fill: theme.tick, fontSize: 11, fontFamily: CHART_FONT, fontWeight: 500 }}
              dy={6}
            />
            <YAxis
              type="number"
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
              width={34}
              domain={[0, yMax]}
              ticks={yTicks}
              tick={{ fill: theme.tick, fontSize: 11, fontFamily: CHART_FONT, fontWeight: 500 }}
            />
            <Tooltip
              cursor={{ stroke: "#2E6BE6", strokeWidth: 1, strokeDasharray: "4 4" }}
              content={<ChartTooltip />}
            />
            <Area
              type="monotone"
              dataKey="students"
              name="Students"
              stroke={STUDENT}
              strokeWidth={2.5}
              strokeLinecap="round"
              fill="url(#studentFill)"
              fillOpacity={1}
              isAnimationActive={false}
              dot={false}
              activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff", fill: STUDENT }}
            />
            <Area
              type="monotone"
              dataKey="teachers"
              name="Teachers"
              stroke={TEACHER}
              strokeWidth={2.5}
              strokeLinecap="round"
              fill="url(#teacherFill)"
              fillOpacity={1}
              isAnimationActive={false}
              dot={false}
              activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff", fill: TEACHER }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="dash-chart-legend dash-chart-legend-bottom">
        <span>
          <i style={{ background: STUDENT }} />
          Students
        </span>
        <span>
          <i style={{ background: TEACHER }} />
          Teachers
        </span>
      </div>
    </section>
  );
}

function MonthAttendanceCard({ variant, title, icon: Icon, accent, breakdown = {}, monthLabel }) {
  const rate = Math.min(100, Math.max(0, breakdown.rate || 0));

  const chartData = useMemo(() => {
    const labels = breakdown.labels || ["Present", "Absent", "Leave", "Holiday"];
    const values = breakdown.values?.length ? breakdown.values : [0, 0, 0, 0];
    return labels
      .map((name, index) => ({
        name,
        value: values[index] ?? 0,
        fill: BREAKDOWN_COLORS[name] || PURPLE,
      }))
      .filter((item) => item.value > 0);
  }, [breakdown]);
  const total = (breakdown.values || []).reduce((sum, value) => sum + Number(value || 0), 0);

  const displayData = chartData.length ? chartData : [{ name: "No data", value: 1, fill: "#e5e7eb" }];
  const isEmpty = !chartData.length;

  return (
    <section className={`dash-card dash-minimal-card dash-chart-panel dash-month-panel dash-month-${variant}`}>
      <div className="dash-month-head">
        <div className="dash-month-head-main">
          <span className="dash-month-icon">
            <Icon size={18} strokeWidth={1.85} />
          </span>
          <div className="dash-month-titles">
            <p className="dash-month-kicker">Attendance</p>
            <h2 className="dash-card-title">{title}</h2>
          </div>
        </div>
        <span className="dash-month-rate">{rate}%</span>
      </div>
      <p className="dash-month-subtitle">{monthLabel}</p>

      <div className="dash-chart-box dash-chart-box-month dash-chart-box-donut">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={[{ name: "track", value: 1 }]}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius="58%"
              outerRadius="82%"
              fill="var(--bw-surface-muted, #eef2f7)"
              stroke="none"
              isAnimationActive={false}
              legendType="none"
              style={{ pointerEvents: "none" }}
            />
            <Pie
              data={displayData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius="58%"
              outerRadius="82%"
              startAngle={90}
              endAngle={-270}
              paddingAngle={isEmpty || chartData.length < 2 ? 0 : 3}
              cornerRadius={6}
              stroke="var(--bw-surface, #fff)"
              strokeWidth={3}
            >
              {displayData.map((entry) => (
                <Cell
                  key={entry.name}
                  fill={isEmpty ? entry.fill : BREAKDOWN_COLORS[entry.name] || entry.fill}
                />
              ))}
            </Pie>
            <Tooltip content={<PieTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        <div className="dash-donut-center dash-month-donut-center">
          <strong style={{ color: accent }}>{rate}%</strong>
          <span>{formatNumber(total)} check-ins</span>
        </div>
      </div>

      <div className="dash-month-legend">
        {(breakdown.labels || ["Present", "Absent", "Leave", "Holiday"]).map((name) => (
          <span key={name}>
            <i style={{ background: BREAKDOWN_COLORS[name] }} />
            {name}
          </span>
        ))}
      </div>
    </section>
  );
}

function SchoolCalendar({ cursor, onMonthChange, marks, monthItems = [], upcoming = [] }) {
  const navigate = useNavigate();
  const today = dayjs();
  const start = cursor.startOf("month");
  const gridStart = start.subtract(start.day(), "day");
  const dayMarks = marksByDay(marks);
  const cells = Array.from({ length: 42 }, (_, i) => {
    const date = gridStart.add(i, "day");
    const inMonth = date.month() === cursor.month();
    const key = date.format("YYYY-MM-DD");
    return {
      key,
      day: date.date(),
      muted: !inMonth,
      isToday: date.isSame(today, "day"),
      marks: inMonth ? dayMarks[key] || [] : [],
    };
  });

  const scheduleItems = monthItems.length ? monthItems : upcoming.slice(0, 5);
  const scheduleTitle = monthItems.length
    ? `This month · ${monthItems.length}`
    : upcoming.length
      ? "Upcoming"
      : "No events";

  return (
    <section className="dash-card dash-minimal-card dash-cal-card">
      <CardHead
        title="Calendar"
        extra={
          <div className="dash-cal-nav">
            <button type="button" aria-label="Previous month" onClick={() => onMonthChange(cursor.subtract(1, "month"))}>
              <FiChevronLeft size={14} />
            </button>
            <span>{cursor.format("MMM YYYY")}</span>
            <button type="button" aria-label="Next month" onClick={() => onMonthChange(cursor.add(1, "month"))}>
              <FiChevronRight size={14} />
            </button>
          </div>
        }
      />
      <div className="dash-cal-grid">
        {["S", "M", "T", "W", "T", "F", "S"].map((dow, i) => (
          <div key={`${dow}-${i}`} className="dash-cal-dow">{dow}</div>
        ))}
        {cells.map((cell) => {
          const hasMarks = cell.marks.length > 0;
          const primary = cell.marks[0];
          const title = cell.marks.map((m) => m.label).filter(Boolean).join(", ");
          const markColor = primary
            ? primary.color || (primary.type === "event" ? EVENT_COLOR : HOLIDAY_COLOR)
            : undefined;
          return (
            <div
              key={cell.key}
              className={`dash-cal-day${cell.muted ? " is-muted" : ""}${cell.isToday ? " is-today" : ""}${hasMarks ? " is-mark" : ""}`}
              title={title || undefined}
            >
              <span style={markColor ? { background: markColor } : undefined}>{cell.day}</span>
              {hasMarks ? (
                <div className="dash-cal-dots">
                  {cell.marks.slice(0, 3).map((mark, index) => (
                    <i
                      key={`${mark.id || mark.label}-${index}`}
                      style={{ background: mark.color || (mark.type === "event" ? EVENT_COLOR : HOLIDAY_COLOR) }}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="dash-cal-legend">
        <span>
          <i style={{ background: HOLIDAY_COLOR }} />
          Holiday
        </span>
        <span>
          <i style={{ background: EVENT_COLOR }} />
          Event
        </span>
        <button type="button" className="dash-cal-link" onClick={() => navigate("/holidays")}>
          Manage
        </button>
      </div>

      <div className="dash-cal-schedule">
        <div className="dash-cal-schedule-head">
          <CalendarDays size={14} />
          <p>{scheduleTitle}</p>
        </div>
        {scheduleItems.length ? (
          <ul className="dash-cal-schedule-list">
            {scheduleItems.map((item) => (
              <li key={item._id}>
                <span
                  className={`dash-cal-pill${item.type === "EVENT" ? " is-event" : " is-holiday"}`}
                >
                  {item.type === "EVENT" ? "Event" : "Holiday"}
                </span>
                <div>
                  <strong>{item.name}</strong>
                  <em>{formatScheduleRange(item)}</em>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="dash-cal-empty">No school events or holidays in this month.</p>
        )}
      </div>
    </section>
  );
}

function Dashboard() {
  const { isDark } = useTheme();
  const [cursor, setCursor] = useState(dayjs());
  const [overviewRange, setOverviewRange] = useState("thisWeek");
  const { data, isLoading, isFetching } = useDashboardOverview({
    month: String(cursor.month() + 1),
    year: String(cursor.year()),
    range: overviewRange,
  });
  const stats = data?.stats || {};
  const attendance = data?.attendance || {};
  const loading = isLoading && !data;
  const monthLabel = cursor.format("MMMM YYYY");
  const overviewSeries = attendance.overview?.labels?.length
    ? attendance.overview
    : attendance.weekly || {};
  const hour = dayjs().hour();
  const greeting =
    hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  return (
    <div className="dash">
      <header className="dash-mobile-intro">
        <div>
          <p className="dash-mobile-kicker">{greeting}</p>
          <h2 className="dash-mobile-title">School overview</h2>
        </div>
        <time className="dash-mobile-date" dateTime={dayjs().format("YYYY-MM-DD")}>
          {dayjs().format("ddd, D MMM")}
        </time>
      </header>

      <section className="dash-stats-wrap">
        <div className="dash-stats">
          {STAT_CARDS.map((card) => {
            const Icon = card.icon;
            return (
              <StatCard
                key={card.key}
                label={card.label}
                value={formatNumber(stats[card.key])}
                icon={Icon}
                tone={card.tone}
                loading={loading}
              />
            );
          })}
        </div>
      </section>

      <WeeklyTrendChart
        series={overviewSeries}
        range={overviewRange}
        onRangeChange={setOverviewRange}
        isDark={isDark}
      />

      <div className={`dash-body${isFetching ? " is-fetching" : ""}`}>
        <div className="dash-bottom-row">
            <MonthAttendanceCard
              variant="student"
              title="Students"
              icon={GraduationCap}
              accent={STUDENT}
              breakdown={attendance.students?.breakdown}
              monthLabel={monthLabel}
            />
            <MonthAttendanceCard
              variant="teacher"
              title="Teachers"
              icon={Presentation}
              accent={TEACHER}
              breakdown={attendance.teachers?.breakdown}
              monthLabel={monthLabel}
            />
            <SchoolCalendar
              cursor={cursor}
              onMonthChange={setCursor}
              marks={data?.calendar?.marks || []}
              monthItems={data?.calendar?.monthItems || []}
              upcoming={data?.calendar?.upcoming || []}
            />
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
